<script setup>
import { computed, ref, onMounted } from 'vue'
import { useRoute } from '@/router/index.js'
import { fields, initDate } from './fields.js'

const route = useRoute()

const emit = defineEmits(['modify-url-params', 'refresh-creative'])

onMounted(() => {
  initCustomValues()
})

const rowHeight = 30
const fieldOptions = ['Auto', 'Custom']
const fieldRefs = ref({})

const expandedField = computed(() => {
  return fields.value.find(x => x.isExpanded)
})

const urlParams = computed(() => {
  const params = route.query
  for (const key in params) {
    if (params[key] !== 'true' && params[key] !== 'false') continue
    params[key] = params[key] === 'true'
  }
  return params
})

const collapse = (field) => {
  setHeightToChildrenHeight(field)
  setHeightToZero(field)
  field.isExpanded = !field.isExpanded
}

const expand = (field) => {
  setHeightToChildrenHeight(field)

  // if another field is open, close it
  if (expandedField.value) collapse(expandedField.value)

  field.isExpanded = !field.isExpanded
}

const toggleExpand = (field) => {
  if (field.isExpanded) collapse(field)
  else expand(field)
}

const getChildrenHeight = (field) => {
  let height = 0
  getEl(field).children.forEach(x => (height += x.offsetHeight))
  return height
}

const onOptionSelect = (field, option) => {
  const action = option.target.value === 'Auto' ? 'delete' : 'set'
  emit('modify-url-params', action, field.name, field.value)
  emit('refresh-creative')

  setTimeout(() => {
    setHeightToChildrenHeight(field)
  }, 10)
}

const customInput = (input, field) => {
  field.value = input
  emit('modify-url-params', 'set', field.name, field.value)
  emit('refresh-creative')
}

const onFocus = (input, field) => {
  if (field.name !== 'date') return
  handleEmptyDateTime(input, field)
}

const handleEmptyDateTime = (input, field) => {
  if (!input) customInput(initDate(), field)
}

const initCustomValues = () => {
  for (const param in urlParams.value) {
    const field = fields.value.find(x => x.name === param)
    if (field) {
      field.selectedOption = 'Custom'
        field.value = urlParams.value[param]
    }
  }
}

const setHeightToChildrenHeight = (field) => {
  getEl(field).style.height = `${getChildrenHeight(field)}px`
}
const setHeightToZero = (field) => {
  getEl(field).style.height = '0px'
}

const getIdx = (field) => {
  return fields.value.findIndex(x => x.name === field.name)
}
const getEl = (field) => {
  return fieldRefs.value[getIdx(field)]
}
</script>

<template>
  <div class="h-100 d-flex flex-column p-3">
    <!-- title -->
    <div class="my-3 text-white pl-4">CONTEXT SIMULATOR</div>
    <!-- context simulator -->
    <div class="context-simulator-container w-100 d-flex flex-column">
      <div
        v-for="(field, idx) in fields"
        :key="idx"
        :style="`
          border-bottom: ${idx !== fields.length - 1 ? '1px solid #CACACA' : ''};
        `"
      >
        <div
          class="cursor-pointer outer-row d-flex justify-content-between align-items-center px-4"
          :style="`height: ${rowHeight}px;`"
          @click="toggleExpand(field)"
        >
          <div>{{ field.display }}</div>
          <font-awesome-icon
            :icon="field.isExpanded ? 'chevron-up' : 'chevron-down'"
            style="height: 10px; width: 10px;"
          />
        </div>

        <!-- expandable field options -->
        <div
          :ref="(el) => (fieldRefs[`${idx}`] = el)"
          class="collapsible overflow-hidden"
          style="background-color: #F0F0F0; height: 0px;"
        >
          <div
            v-for="(option, optIdx) in fieldOptions"
            :key="optIdx"
            class="inner-row cursor-pointer d-flex align-items-center px-4"
          >
            <input
              :id="`${idx}${optIdx}`"
              v-model="field.selectedOption"
              class="mr-4 cursor-pointer"
              :style="`height: ${rowHeight}px;`"
              :value="option"
              type="radio"
              @input="(option) => onOptionSelect(field, option)"
            />
            <label
              :for="`${idx}${optIdx}`"
              style="margin: 0 !important;"
              class="cursor-pointer"
            >
              {{ option }}
            </label>
          </div>

          <!-- Custom Options -->
          <div
            v-if="field.selectedOption === 'Custom'"
            class="cursor-pointer px-4 pb-3 d-flex"
          >
            <div
              v-for="(customOption, cidx) in field.customOptions"
              :key="cidx"
              class="mr-3"
              style="flex: 1;"
            >
              <select
                v-if="customOption.type === 'select'"
                :value="customOption.value || field.value"
                @input="e => customInput(e.target.value, field, customOption)"
              >
                <option
                  v-for="(opt, oidx) in customOption.options"
                  :key="oidx"
                  :value="opt"
                >
                  {{ opt }}
                </option>
              </select>
              <input
                v-else
                :value="customOption.value || field.value"
                :type="customOption.type"
                :placeholder="customOption.placeholder || ''"
                style="width: 100%"
                @input="e => customInput(e.target.value, field, customOption)"
                @focus="e => onFocus(e.target.value, field, customOption)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.context-simulator-container {
  border-radius: 5px;
  background-color: #FAFAFA;
  color: #6D6D6D;
  overflow: hidden;
}
.outer-row {
  z-index: 2;
}
.inner-row {
  background-color: #F0F0F0;
  border-top: 1px solid #CACACA;
}
.collapsible {
  transition: height 300ms ease-in-out;
}
.cursor-pointer {
  cursor: pointer;
}
.overflow-hidden {
  overflow: hidden;
}
</style>
